// src/services/indexedDBService.js
/**
 * IndexedDB Service for Trusted Device Caching
 * Stores workspaces, documents, and notes locally for offline access
 */

const DB_NAME = 'ThinkingAppCache';
const DB_VERSION = 2;

// Object store names
const STORES = {
  WORKSPACES: 'workspaces',
  DOCUMENTS: 'documents',
  NOTES: 'notes',
  METADATA: 'metadata'
};

class IndexedDBService {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  /**
   * Initialize the database
   */
  async init() {
    if (this.initPromise) return this.initPromise;
    
    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialized');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Workspaces store
        if (!db.objectStoreNames.contains(STORES.WORKSPACES)) {
          const workspaceStore = db.createObjectStore(STORES.WORKSPACES, { keyPath: 'public_id' });
          workspaceStore.createIndex('user_id', 'user_id', { unique: false });
          workspaceStore.createIndex('modified_date', 'modified_date', { unique: false });
          workspaceStore.createIndex('primary_category_id', 'primary_category_id', { unique: false });
        }

        // Documents store
        if (!db.objectStoreNames.contains(STORES.DOCUMENTS)) {
          const documentStore = db.createObjectStore(STORES.DOCUMENTS, { keyPath: 'public_id' });
          documentStore.createIndex('workspace_public_id', 'workspace_public_id', { unique: false });
          documentStore.createIndex('modified_date', 'modified_date', { unique: false });
        }

        // Notes store (excluding image notes)
        if (!db.objectStoreNames.contains(STORES.NOTES)) {
            const noteStore = db.createObjectStore(STORES.NOTES, { keyPath: 'id' });
            noteStore.createIndex('workspace_id', 'workspace_id', { unique: false });
            noteStore.createIndex('workspace_public_id', 'workspace_public_id', { unique: false }); // ← ADD THIS
          }

        // Metadata store (for sync tracking)
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: 'key' });
        }

        console.log('✅ IndexedDB schema created');
      };
    });

    return this.initPromise;
  }

  /**
   * Generic method to add or update data in a store
   */
  async put(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Generic method to get data from a store
   */
  async get(storeName, key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Generic method to get all data from a store
   */
  async getAll(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Generic method to delete data from a store
   */
  async delete(storeName, key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data from a store
   */
  async clear(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all cached data (on logout)
   */
  async clearAll() {
    await this.init();
    const stores = [STORES.WORKSPACES, STORES.DOCUMENTS, STORES.NOTES, STORES.METADATA];
    
    for (const store of stores) {
      await this.clear(store);
    }
    
    console.log('🗑️ All cache cleared');
  }

  // ============================================================================
  // Workspace-specific methods
  // ============================================================================

  async saveWorkspace(workspace) {
    return this.put(STORES.WORKSPACES, workspace);
  }

  async getWorkspace(publicId) {
    return this.get(STORES.WORKSPACES, publicId);
  }

  async getAllWorkspaces() {
    return this.getAll(STORES.WORKSPACES);
  }

  async deleteWorkspace(publicId) {
    return this.delete(STORES.WORKSPACES, publicId);
  }

  // ============================================================================
  // Document-specific methods
  // ============================================================================

  async saveDocument(document) {
    return this.put(STORES.DOCUMENTS, document);
  }

  async getDocument(publicId) {
    return this.get(STORES.DOCUMENTS, publicId);
  }

  async getAllDocuments() {
    return this.getAll(STORES.DOCUMENTS);
  }

  async getDocumentsByWorkspace(workspacePublicId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.DOCUMENTS], 'readonly');
      const store = transaction.objectStore(STORES.DOCUMENTS);
      const index = store.index('workspace_public_id');
      const request = index.getAll(workspacePublicId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDocument(publicId) {
    return this.delete(STORES.DOCUMENTS, publicId);
  }

  // ============================================================================
  // Note-specific methods (excluding image notes)
  // ============================================================================

  async saveNote(note) {
    // Don't cache image notes
    if (note.type === 'image') {
      console.log('⏭️ Skipping image note cache:', note.id);
      return;
    }
    return this.put(STORES.NOTES, note);
  }

  async getNote(id) {
    return this.get(STORES.NOTES, id);
  }

  async getAllNotes() {
    return this.getAll(STORES.NOTES);
  }

  async getNotesByWorkspace(workspacePublicId) {  // ← Also rename parameter
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.NOTES], 'readonly');
      const store = transaction.objectStore(STORES.NOTES);
      const index = store.index('workspace_public_id');  // ← CHANGED FROM 'workspace_id'
      const request = index.getAll(workspacePublicId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteNote(id) {
    return this.delete(STORES.NOTES, id);
  }

  // ============================================================================
  // Metadata methods (for sync tracking)
  // ============================================================================

  async setMetadata(key, value) {
    return this.put(STORES.METADATA, { key, value, updated_at: new Date().toISOString() });
  }

  async getMetadata(key) {
    const result = await this.get(STORES.METADATA, key);
    return result ? result.value : null;
  }

  async getLastSyncTime() {
    return this.getMetadata('last_sync_time');
  }

  async setLastSyncTime(timestamp) {
    return this.setMetadata('last_sync_time', timestamp);
  }

  // ============================================================================
  // Search methods
  // ============================================================================

  /**
   * Search across all cached data
   */
  async search(query) {
    await this.init();
    const results = [];
    const lowerQuery = query.toLowerCase();

    // Search workspaces
    const workspaces = await this.getAllWorkspaces();
    workspaces.forEach(workspace => {
      if (workspace.name.toLowerCase().includes(lowerQuery)) {
        results.push({
          type: 'workspace',
          id: workspace.public_id,
          title: workspace.name,
          content: '',
          workspace_id: workspace.public_id,
          workspace_name: workspace.name,
          modified_date: workspace.modified_date
        });
      }
    });

    // Search documents
    const documents = await this.getAllDocuments();
    documents.forEach(doc => {
      const titleMatch = doc.title.toLowerCase().includes(lowerQuery);
      const contentMatch = doc.content && doc.content.toLowerCase().includes(lowerQuery);
      
      if (titleMatch || contentMatch) {
        const workspace = workspaces.find(w => w.public_id === doc.workspace_public_id);
        results.push({
          type: 'document',
          id: doc.public_id,
          title: doc.title,
          content: doc.content ? doc.content.substring(0, 200) : '',
          workspace_id: doc.workspace_public_id,
          workspace_name: workspace ? workspace.name : 'Unknown',
          modified_date: doc.modified_date
        });
      }
    });

    // Search notes (excluding image notes)
    const notes = await this.getAllNotes();
    notes.forEach(note => {
      if (note.type !== 'image' && note.content && note.content.toLowerCase().includes(lowerQuery)) {
        const workspace = workspaces.find(w => w.id === note.workspace_id);
        results.push({
          type: 'note',
          id: note.id,
          title: note.title || 'Untitled Note',
          content: note.content.substring(0, 200),
          workspace_id: workspace ? workspace.public_id : null,
          workspace_name: workspace ? workspace.name : 'Unknown',
          created_at: note.created_at
        });
      }
    });

    // Sort by modified date (most recent first)
    results.sort((a, b) => {
      const dateA = new Date(a.modified_date || a.created_at || 0);
      const dateB = new Date(b.modified_date || b.created_at || 0);
      return dateB - dateA;
    });

    return results;
  }
}

// Export singleton instance
const indexedDBService = new IndexedDBService();
export default indexedDBService;